import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  CloudAccountGetResponse,
  CloudProviders,
} from '@ariksa/cloud-account/api';
import {
  Box,
  Center,
  HStack,
  Stack,
  Text,
  useDisclosure,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import {
  each,
  filter,
  find,
  forEach,
  identity,
  includes,
  isEmpty,
  toArray,
} from 'lodash';
import map from 'lodash/map';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-use';

import { ScanVaultScannerInfo } from 'api/scan-vault/client';
import {
  EllipsisText,
  formatTooltip,
  getIcon,
  getProviderName,
  IconTypes,
  renderStatusTag,
  renderTableHeaderWithLoader,
  renderTime,
  renderTooltipFieldValue,
  StackedCell,
  timeLabel,
  timeTooltipLabel,
  WithResourceIcon,
} from 'app/components';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import { selectApp } from 'containers/App/selectors';
import { selectCloudAccounts } from 'containers/Setup/CloudAccounts/selectors';
import { selectSharedState } from 'containers/SharedState/selectors';

import { CloudAccountCard } from './Components/CloudAccountCard';

export const renderRow = (row, isDevOrTest) => {
  return <CloudAccountCard row={row} isDevOrTestEnv={isDevOrTest} />;
};

export const renderStatus = ({ value, row }) => {
  return (
    <>
      {value === 'UNAUTHORIZED' || value === 'PENDING' ? (
        <CustomTooltip>{renderStatusTag(value)}</CustomTooltip>
      ) : (
        renderStatusTag(value)
      )}
    </>
  );
};

export const onBoardRender = ({ value }) => {
  if (value.success === undefined) {
    return '-';
  }
  return (
    <StackedCell
      upper={value.success + ' out of'}
      lower={value.total + ' account' + (value.total > 1 ? 's' : '')}
    />
  );
};

export const useCloudAccountTableColumns = () => {
  const { vulnerabilityScanner, dataScanner } = useSelector(
    selectCloudAccounts,
  );
  const { snapshotStatus, centralScanners } = useSelector(selectSharedState);
  const showDeployedOutposts = useDisclosure();
  const [currentRow, setCurrentRow] = useState<Record<string, any>>({});
  const location = useLocation();

  const name = {
    header: <Box pl={10}>Name</Box>,
    accessor: 'name',
    align: 'left',
    render: ({ row }) => (
      <WithResourceIcon
        resourceType={row.cloud_type}
        iconFilled={false}
        iconSize="regular"
        border={false}
      >
        <StackedCell upper={row?.name} lower={row.cloud_account_id} />
      </WithResourceIcon>
    ),
  };

  const type = {
    header: 'Type',
    accessor: 'aws.account_type',
    align: 'left',
    render: ({ row }) => {
      return `${row[row.cloud_type]?.account_type ?? '-'}`;
    },
  };

  const added = {
    header: 'Added',
    render: ({ row }) => (
      <StackedCell
        upper={row.created_by}
        lower={timeLabel(row?.created_date)}
        lowerTooltip={timeTooltipLabel(row?.created_date)}
      />
    ),
    align: 'left',
    styles: {
      minW: '200px',
    },
  };

  const onboarded: TableColumnProps = {
    header: 'Onboarded',
    accessor: 'count',
    render: onBoardRender,
    align: 'left',
  };

  const snapshotStatusColumn = {
    header: renderTableHeaderWithLoader(
      'Sync Status',
      snapshotStatus.isLoading,
    ),
    align: 'left',
    render: ({ row }) => {
      return row?.status?.code !== 'SUCCESS' ? (
        renderStatusTag(row?.status?.code)
      ) : (
        <StackedCell
          upper={
            snapshotStatus.isLoading
              ? '-'
              : renderStatusTag(snapshotStatus.data?.[row.uuid]?.status ?? '')
          }
          lower={
            snapshotStatus.isLoading
              ? '-'
              : renderTime({
                  value: snapshotStatus.data?.[row.uuid]?.created_at,
                })
          }
          showUpperTooltip={false}
          showLowerTooltip={false}
        />
      );
    },
  };

  const nextScan = {
    header: 'Next Discovery',
    align: 'left',
    render: ({ row }) =>
      row?.status?.code !== 'SUCCESS' ? (
        '-'
      ) : row?.is_paused ? (
        renderStatusTag(
          row?.is_paused ? 'PAUSED' : 'SCHEDULED',
          row?.is_paused && row?.paused_till ? 'orange.300' : '',
        )
      ) : (
        <StackedCell
          showUpperTooltip={false}
          showLowerTooltip={false}
          upper={
            row?.is_paused ? (
              (!!row?.paused_till &&
                dayjs(row?.paused_till).diff(dayjs(), 'year')) < 1 ? (
                <HStack>
                  <Box>Until</Box>
                  <Box>{renderTime({ value: row?.paused_till })}</Box>
                </HStack>
              ) : (
                'Indefinitely'
              )
            ) : (
              renderTime({ value: row?.next_discovery_time })
            )
          }
          lower={
            row.discovery_interval
              ? `Frequency: ${row.discovery_interval / 60} hours`
              : '-'
          }
        />
      ),
  };

  const frequency = {
    header: 'Frequency',
    render: ({ row }) => row.discovery_interval / 60 + ' hours',
  };

  const outpostDeployed = {
    header: renderTableHeaderWithLoader(
      'Ariksa Technologies',
      vulnerabilityScanner.isLoading ||
        dataScanner.isLoading ||
        centralScanners.isLoading,
    ),
    render: ({ row }) => {
      const renderIcon = (type, isDeployed, tooltip) => (
        <CustomTooltip
          label={
            <Text w="full" textAlign="center">
              {tooltip}
            </Text>
          }
          w={32}
        >
          <Box
            color={
              isDeployed
                ? type === IconTypes.Vulnerability
                  ? 'critical'
                  : type === IconTypes.Remediate
                  ? 'green.300'
                  : 'primary'
                : 'gray.250'
            }
            //bg={isDeployed ? 'green.300' : 'gray.100'}
            boxSize={6}
            p={0}
            cursor={
              isDeployed && type === IconTypes.Data ? 'pointer' : 'default'
            }
            onClick={() => {
              if (isDeployed && type === IconTypes.Data) {
                showDeployedOutposts.onOpen();
                setCurrentRow(row);
              }
            }}
          >
            <Center w="full" h="full">
              {getIcon(type)}
            </Center>
          </Box>
        </CustomTooltip>
      );
      let vulnerability: Record<string, any>[] = filter(
        vulnerabilityScanner.data?.[row?.uuid],
        (o: ScanVaultScannerInfo) => o?.status === 'Active',
      );
      if (!isEmpty(centralScanners.data)) {
        const v = find(centralScanners.data, o => o.id === row?.uuid);
        if (!isEmpty(v)) vulnerability = [...vulnerability, v];
      }
      const data = [
        ...filter(
          dataScanner.data?.scanners?.[row?.uuid],
          o => o.status === 'Active',
        ),
        ...filter(
          dataScanner.data?.assignments,
          o => o?.non_cloud_account_id === row?.uuid,
        ),
      ];
      const vulnerabilityScannerDeployed = !isEmpty(vulnerability);
      const dataScannerDeployed = !isEmpty(data);
      return (
        //row.cloud_type !== CloudProviders.Github &&
        //row.cloud_type !== CloudProviders.Gitlab &&
        row.cloud_type !== CloudProviders.GitlabIac &&
        row.cloud_type !== CloudProviders.GithubIac &&
        //row.cloud_type !== CloudProviders.Bitbucket &&
        row.cloud_type !== CloudProviders.BitbucketIac &&
        row.cloud_type !== CloudProviders.Okta && (
          <HStack>
            {(row.cloud_type === CloudProviders.Gcp ||
              row.cloud_type === CloudProviders.Aws ||
              row.cloud_type === CloudProviders.Azure) &&
              renderIcon(
                IconTypes.Vulnerability,
                vulnerabilityScannerDeployed,
                <Stack>
                  <Box>
                    Outpost for Vulnerabilities
                    <Text
                      color={
                        vulnerabilityScannerDeployed ? 'green.300' : 'orange'
                      }
                    >
                      {vulnerabilityScannerDeployed ? ' ' : ' Not '}
                      Deployed
                    </Text>
                  </Box>
                  {vulnerabilityScannerDeployed && (
                    <Text>
                      <Text color="orange">Last scan: </Text>
                      <Text px={4}>
                        {renderTime({
                          value:
                            vulnerability?.[0]?.last_seen ??
                            vulnerability?.[0]?.last_scan_at,
                        })}
                      </Text>
                    </Text>
                  )}
                </Stack>,
              )}
            {(row.cloud_type === CloudProviders.Aws ||
              row.cloud_type === CloudProviders.Gitlab ||
              row.cloud_type === CloudProviders.Github ||
              row.cloud_type === CloudProviders.Bitbucket ||
              row.cloud_type === CloudProviders.Snowflake) &&
              renderIcon(
                IconTypes.Data,
                dataScannerDeployed,
                <Box>
                  Outpost for Data
                  <Text color={dataScannerDeployed ? 'green.300' : 'orange'}>
                    {dataScannerDeployed ? '' : 'Not '}
                    Deployed
                  </Text>
                </Box>,
              )}
            {(row.cloud_type === CloudProviders.Aws ||
              row.cloud_type === CloudProviders.Gcp) &&
              renderIcon(
                IconTypes.Jit,
                row?.jit_status,
                <Box>
                  Just-In-Time (JIT) Access
                  <Text color={row?.jit_status ? 'green.300' : 'orange'}>
                    {row?.jit_status ? '' : 'Not '}
                    Enabled
                  </Text>
                </Box>,
              )}
            {(row.cloud_type === CloudProviders.Aws ||
              row.cloud_type === CloudProviders.Azure) &&
              renderIcon(
                IconTypes.Remediate,
                row?.remediation_status,
                <Box>
                  Remediation
                  <Text
                    color={
                      row?.remediation_status === 'SUCCESS'
                        ? 'green.300'
                        : 'orange'
                    }
                  >
                    {row?.remediation_status === 'INITIATED'
                      ? 'Initiated'
                      : row?.remediation_status === 'SUCCESS'
                      ? 'Enabled'
                      : 'Not Enabled'}
                  </Text>
                </Box>,
              )}
          </HStack>
        )
      );
    },
    align: 'left',
  };

  const columns: TableColumnProps[] = [
    name,
    type,
    added,
    onboarded,
    snapshotStatusColumn,
    //nextScan,
    //frequency,
    outpostDeployed,
    {
      header: 'Actions',
      accessor: 'id',
      render: ({ row }) =>
        renderRow(row, location.hostname !== 'app.ariksa.io'),
      align: 'center',
      styles: { width: 90 },
    },
  ];
  const memberColumns = [
    name,
    type,
    added,
    snapshotStatusColumn,
    //nextScan,
    outpostDeployed,
  ];

  return { columns, memberColumns, showDeployedOutposts, currentRow };
};

export const snapshotColumns = [
  {
    header: 'Start Time',
    accessor: 'start_time',
    render: renderTime,
    align: 'left',
    styles: {
      minWidth: '200px',
    },
  },
  {
    header: 'Status',
    accessor: 'status',
    render: renderStatus,
    styles: {
      minWidth: '200px',
    },
  },
];

export const useCloudAccountId = () => {
  const { cloudAccounts } = useSelector(selectApp);
  const [accountMapping, setAccountMapping] = useState<
    Record<string, CloudAccountGetResponse>
  >({});
  const [cloudAccountIDMapping, setCloudAccountIDMapping] = useState<
    Record<string, CloudAccountGetResponse>
  >({});
  const [providers, setProviders] = useState<string[]>([]);

  useEffect(() => {
    const items: Record<string, CloudAccountGetResponse> = {};
    let providers = new Set<string>();
    forEach(cloudAccounts.data, o => {
      items[o.uuid] = o;
      providers.add(o.cloud_type);
    });
    setAccountMapping(items);
    setProviders(toArray(providers));
  }, [cloudAccounts.data]);

  useEffect(() => {
    const items: Record<string, CloudAccountGetResponse> = {};
    forEach(cloudAccounts.data, o => (items[o.cloud_account_id!] = o));
    setCloudAccountIDMapping(items);
  }, [cloudAccounts.data]);

  const getAccountOption = useCallback(
    uuid => {
      const a = accountMapping?.[uuid];
      return {
        label: a?.name + ' (' + a?.cloud_account_id + ')',
        value: uuid,
        data: a,
        icon: getIcon(a?.cloud_type!),
      };
    },
    [accountMapping],
  );

  const mapToCloudId = useCallback(
    (uuids: string[] = []) => {
      return (
        uuids
          .map(
            u =>
              cloudAccounts.data.find(a => a.uuid === u?.trim())
                ?.cloud_account_id,
          )
          .filter(identity) ?? []
      );
    },
    [cloudAccounts],
  );

  const toCloudAccountId = useCallback(
    uuid => {
      return accountMapping[uuid]?.cloud_account_id ?? '-';
    },
    [accountMapping],
  );

  const toCloudAccountName = useCallback(
    uuid => {
      const data = accountMapping[uuid];
      return data?.name ?? data?.cloud_account_id ?? '-';
    },
    [accountMapping],
  );

  const renderCloudAccountTooltip = useCallback(
    uuid => {
      const account = accountMapping[uuid];
      const name = account?.name;
      const id = account?.cloud_account_id;
      return formatTooltip({
        header: 'ACCOUNT',
        content: (
          <Stack>
            {name && renderTooltipFieldValue('Name', name)}
            {renderTooltipFieldValue('ID', id)}
            {renderTooltipFieldValue(
              'Cloud',
              getProviderName(account?.cloud_type),
            )}
            {!name && !id && renderTooltipFieldValue('Account UUID', uuid)}
          </Stack>
        ),
        width: 64,
      });
    },
    [accountMapping],
  );

  const renderStackedCloudAccountWithCloud = useCallback(
    (uuid, isInline = false, cloud = '') => {
      const account = accountMapping[uuid];
      const name = account?.name;
      const id = account?.cloud_account_id;
      const provider = cloud || account?.cloud_type;
      const renderName = () => (
        <Box flex={1} overflow="hidden" textOverflow="ellipsis">
          {name || id || uuid}
        </Box>
      );
      return (
        <CustomTooltip label={renderCloudAccountTooltip(uuid)}>
          <Stack
            spacing={isInline ? 2 : 0}
            direction={isInline ? 'row' : 'column'}
            w="full"
          >
            {!isInline && renderName()}
            <Center boxSize={5}>
              <Center>{getIcon(provider!)}</Center>
            </Center>
            {isInline && renderName()}
          </Stack>
        </CustomTooltip>
      );
    },
    [accountMapping, renderCloudAccountTooltip],
  );

  const renderAccountNameAndID = useCallback(
    uuid => {
      const account = accountMapping[uuid];
      const name = account?.name;
      const id = account?.cloud_account_id;
      const provider = account?.cloud_type;
      return (
        <CustomTooltip label={renderCloudAccountTooltip(uuid)}>
          <HStack
            //spacing={isInline ? 2 : 0}
            w="full"
          >
            <Center boxSize={5}>
              <Center>{getIcon(provider!)}</Center>
            </Center>
            <EllipsisText flex={1}>
              {name || uuid} ({id})
            </EllipsisText>
          </HStack>
        </CustomTooltip>
      );
    },
    [accountMapping, renderCloudAccountTooltip],
  );

  const renderStackedAccountNameID = useCallback(
    (uuid: string, alignIcon = 'start') => {
      const account = accountMapping[uuid];
      const name = account?.name;
      const id = account?.cloud_account_id;
      const displayName = name || uuid;
      const displayId = id || uuid;
      return (
        <CustomTooltip label={renderCloudAccountTooltip(uuid)}>
          <StackedCell
            upper={
              <HStack spacing={2}>
                {alignIcon === 'start' && (
                  <Center boxSize={5}>
                    <Center>{getIcon(account?.cloud_type!)}</Center>
                  </Center>
                )}
                <EllipsisText showTooltip={false}>{displayName}</EllipsisText>
              </HStack>
            }
            iconProps={
              alignIcon === 'center'
                ? {
                    resourceType: account?.cloud_type!,
                    iconFilled: false,
                    iconSize: 'xs',
                  }
                : undefined
            }
            lower={displayId}
            showUpperTooltip={false}
            showLowerTooltip={false}
          />
        </CustomTooltip>
      );
    },
    [accountMapping, renderCloudAccountTooltip],
  );

  const renderStackedCloudAccountIdWithIcon = useCallback(
    (uuid: string) => renderStackedAccountNameID(uuid),
    [renderStackedAccountNameID],
  );

  const renderCloudsFromAccountIds = useCallback(
    (accountIds: string[]) => {
      let clouds = new Set();
      each(accountIds, o => clouds.add(accountMapping?.[o]?.cloud_type));
      return (
        <HStack>
          {map(toArray(clouds), o => (
            <CustomTooltip label={getProviderName(o)}>
              <Box boxSize={4} pt={o === CloudProviders.Aws ? 1 : 0}>
                <Center>{getIcon(o as string)}</Center>
              </Box>
            </CustomTooltip>
          ))}
        </HStack>
      );
    },
    [accountMapping],
  );

  const renderAccountWithVPCAndRegion = useCallback(
    (insight: Record<string, any>) => {
      const account = accountMapping[insight?.account];
      const name = account?.name;
      const id = account?.cloud_account_id;
      const provider = account?.cloud_type;
      const vpc = insight?.vpc || insight?.vpc_id;

      return (
        <CustomTooltip
          label={
            <Stack>
              {renderTooltipFieldValue('Account Name', name)}
              {renderTooltipFieldValue('Account ID', id)}
              {renderTooltipFieldValue('Provider', getProviderName(provider))}
              {insight?.region &&
                renderTooltipFieldValue('Region', insight?.region)}
              {vpc && renderTooltipFieldValue('VPC', vpc)}
            </Stack>
          }
        >
          <HStack>
            <Center boxSize={5} flexShrink={0}>
              <Center>{getIcon(provider!)}</Center>
            </Center>
            <EllipsisText flex={1} showTooltip={false}>
              {name ?? insight?.account} / {id}{' '}
              {insight?.region && ' / ' + insight?.region}
              {vpc && ' / ' + vpc}
            </EllipsisText>
          </HStack>
        </CustomTooltip>
      );
    },
    [accountMapping],
  );

  const accountOptions = useCallback(
    (providers?: CloudProviders[], showIdWithName = true) => {
      let accounts = cloudAccounts.data;
      if (!!providers) {
        accounts = filter(cloudAccounts.data, (o: CloudAccountGetResponse) =>
          providers?.includes(o.cloud_type!),
        );
      }
      return map(accounts, a => ({
        label: a.name + (showIdWithName ? ` (${a.cloud_account_id})` : ''),
        value: a.uuid,
        data: a,
        icon: getIcon(a.cloud_type!),
      }));
    },
    [cloudAccounts.data],
  );

  return {
    mapToCloudId,
    toCloudAccountId,
    toCloudAccountName,
    renderStackedCloudAccountWithCloud,
    accountMapping,
    renderStackedAccountNameID,
    renderStackedCloudAccountIdWithIcon,
    renderCloudAccountTooltip,
    getAccountOption,
    renderCloudsFromAccountIds,
    renderAccountWithVPCAndRegion,
    accountOptions,
    renderAccountNameAndID,
    cloudAccountIDMapping,
    providers,
  };
};

export const CloudAccountName = ({ accountId }) => {
  const { toCloudAccountName, toCloudAccountId } = useCloudAccountId();
  return (
    <CustomTooltip
      label={`${toCloudAccountName(accountId)} (${toCloudAccountId(
        accountId,
      )})`}
    >
      <Box>{toCloudAccountName(accountId)}</Box>
    </CustomTooltip>
  );
};

export const isSaaSAccount = (account: CloudAccountGetResponse) => {
  if (!account) return false;
  return [
    CloudProviders.Github,
    CloudProviders.Gitlab,
    CloudProviders.GithubIac,
    CloudProviders.GitlabIac,
    CloudProviders.Bitbucket,
    CloudProviders.BitbucketIac,
    CloudProviders.Snowflake,
  ].includes(account.cloud_type as any);
};
